import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Mail, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api/client';

export default function MediaCard({ media, onEdit }) {
  const queryClient = useQueryClient();
  const isEnvelope = media.type === 'ENVELOPE';
  const Icon = isEnvelope ? Mail : FileText;

  const deleteMutation = useMutation({
    mutationFn: () => api.entities.Media.delete(media.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.success('Media removed');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to remove media'),
  });

  const handleDelete = () => {
    if (window.confirm(`Remove "${media.name}"?`)) deleteMutation.mutate();
  };

  return (
    <Card className="border-slate-200 hover:border-amber-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-slate-900">{media.name}</h4>
                <Badge variant="secondary" className="text-xs">
                  {isEnvelope ? 'Envelope' : 'Label'}
                </Badge>
              </div>
              <p className="text-sm text-slate-600">
                {media.width}&quot; × {media.height}&quot;
              </p>
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(media)}
              className="hover:bg-amber-50 hover:text-amber-600"
              aria-label={`Edit ${media.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="hover:bg-red-50 hover:text-red-600"
              aria-label={`Remove ${media.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
